/**
 * Search dropdown component module for Search component.
 * @module shared/components/Search/SearchDropdown
 *
 */
import React, {useRef, useEffect} from 'react';
import PropTypes from 'prop-types';
import Select, {components} from 'react-select';
import styled, {withTheme} from 'styled-components/macro';
import {fill} from '../cssUtils';
import {v4 as uuid} from 'uuid';

const _Wrapper = styled.div`
  ${props => (props.optionsLength >= 2) ? null : 'display: none;'}
  height: 20px;
  ${fill()};
`;

const _OptionLabel = styled.div`
  font-family: ${props => props.theme.bodyText.name};
  font-size: ${props => props.theme.bodyText.size};
  color: ${props => props.theme.dark ? 'rgba(255, 255, 255, 0.9)' : 'rgba(0, 0, 0, 0.9)'};
  text-align: left;
`;

const Option = prop => (
  <components.Option {...prop}>
    <_OptionLabel>{prop.data.label}</_OptionLabel>
  </components.Option>
);

/**
 * Search dropdown component based on 'react-select' to create dropdown menu
 * @name SearchDropdown
 */
const SearchDropdown = (props) => {
  const {options, onChange, dropdownPositionInfo} = props;

  const styling = {
    control: styles => {
      delete styles.minHeight;
      return {
        ...styles,
        height: '100%',
        border: 'none',
        backgroundColor: 'transparent',
        boxShadow: 'none',
        cursor: 'pointer',
      };
    },

    valueContainer: () => ({
      width: 0,
      margin: 0,
      padding: 0,
      opacity: 0,
    }),

    indicatorsContainer: styles => ({
      ...styles,
      height: '20px',
    }),

    indicatorSeparator: () => ({
      display: 'none',
    }),

    dropdownIndicator: styles => ({
      ...styles,
      color: props.theme.dark ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)',
      margin: '5px 5px 5px 0',
      padding: 0,

      ':hover': {
        opacity: 0.8,
        cursor: 'pointer',
      },
    }),

    menu: styles => {
      delete styles.marginTop;
      delete styles.marginBottom;
      return {
        ...styles,
        right: '-35px',
        width: `${dropdownPositionInfo.width}px`,
        backgroundColor: props.theme.tileBackgroundColor,
        borderRadius: '4px',
        borderStyle: 'none',
        boxShadow: props.theme.neumorphic ? '1px 1px 0 0 rgba(0, 0, 0, 0.4), -1px -1px 0 0 rgba(255, 255, 255, 0.1), 1px 2px 6px 0 rgba(0, 0, 0, 0.5)' : '0px 1px 4px rgba(0, 0, 0, 0.5)',
        marginTop: props.theme.neumorphic ? '3px' : '',
      };
    },

    option: (styles, state) => {
      delete styles[':active'];
      const neumorphicStyling = {};

      if (props.theme.neumorphic) {
        neumorphicStyling.width = 'fill';
        neumorphicStyling.height = 'fill';
        neumorphicStyling.margin = '0 3px';
        neumorphicStyling.borderRadius = '2px';
        if (state.isSelected) {
          neumorphicStyling.boxShadow = props.theme.dark ? 'inset -1px -1px 0 0 rgba(255, 255, 255, 0.18), inset 1px 1px 0 0 rgba(0, 0, 0, 0.3)' : 'inset -1px -1px 0 0 rgba(255, 255, 255, 0.18), inset 1px 1px 0 0 rgba(0, 0, 0, 0.3)';
          neumorphicStyling.backgroundColor = 'rgba(255, 255, 255, 0.1)';
        }
        if (state.isFocused) {
          neumorphicStyling.backgroundColor = props.theme.dark ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.15)';
        }
      }

      const bgColor = () => {
        if (state.isSelected) {
          return props.theme.dark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)';
        }
        if (state.isFocused) {
          return props.theme.dark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)';
        }
        return 'transparent';
      };
      return {
        ...styles,
        padding: '10px',
        fontFamily: props.theme.bodyText.name,
        fontSize: props.theme.bodyText.size,
        color: props.theme.dark ? 'rgba(255, 255, 255, 0.9)' : 'rgba(0, 0, 0, 0.9)',
        textAlign: 'left',
        backgroundColor: bgColor(),
        cursor: 'pointer',
        ...neumorphicStyling,
      };
    },
  };

  const portalId = useRef('cb-search-dropdown-' + uuid());

  // create and append the portal element
  useEffect(() => {
    const creating = document.createElement('div');
    creating.id = portalId.current;
    document.body.appendChild(creating);

    return () => {
      const removing = document.getElementById(portalId.current);
      if (removing) {
        document.body.removeChild(removing);
      }
    };
  }, [portalId.current]);

  return <_Wrapper optionsLength={options.length}>
    <Select
      closeMenuOnScroll
      tabSelectsValue={false}
      isClearable={false}
      options={options}
      placeholder={''}
      menuPortalTarget={document.getElementById(portalId.current)}
      styles={{
        ...styling,
        menuPortal: base => ({...base, zIndex: 999}),
      }}
      onChange={onChange}
      components={{Option}}
    />
  </_Wrapper>;
};

export default withTheme(SearchDropdown);

SearchDropdown.propTypes = {
  /**
   * Set of options
   */
  options: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
    ]).isRequired,
    value: PropTypes.isRequired,
  })).isRequired,
  /**
   * On Change Function
   */
  onChange: PropTypes.func.isRequired,
  /**
   * Object with Position for the Search, needed to correctly size the Dropdown Menu.
   */
  dropdownPositionInfo: PropTypes.object.isRequired,
  /**
   * The theme prop provided by the ThemeProvider
   */
  theme: PropTypes.object,
};
